'use client'

import React, { useEffect, useMemo, useState } from 'react'
import Image from 'next/image'
import { cn } from '@/lib/utils'
import { useBranding } from '@/hooks/useBranding'
import { AuthCard } from '@/components/auth/AuthCard'
import { AuthSignupCard } from '@/components/auth/AuthSignupCard'
import { VideoEmbed } from '@/components/media/VideoEmbed'
import { useImageManager } from '@/components/image/ImageManagerProvider'
import { motion, AnimatePresence } from 'framer-motion'
import { useCurrentUser } from '@/hooks/useCurrentUser'
import { useRouter } from 'next/navigation'
import { useMasterAgentName } from '@/hooks/useMasterAgentName'
import { API_CONFIG } from '@/config/api'
import {
  ImagePlus,
  Trash2,
  Sun,
  Moon,
  Eye,
  Video,
  Check,
  ArrowLeft,
} from 'lucide-react'

import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog'

type StartPayload = {
  sellerId: number
  home: any
  isOwner: boolean
  isBrandless: boolean
  hasWhitelabel: boolean
  companystatus: number
}

type AuthView = 'login' | 'signup'

type StartDraft = {
  head: string
  text: string
  video: string
  theme: 'dark' | 'light'
  images_bg: string[] 
  action: string
}

function resolveBrandLogoHorizontal(
  src?: string | null,
  siteOrigin?: string
) {
  if (!src) return ''

  const clean = src.trim()

  if (/^https?:\/\//i.test(clean)) return clean

  if (clean.startsWith('wkseller/')) {
    return `${siteOrigin}/image/${clean}`
  }

  if (clean.startsWith('images/')) {
    return `${siteOrigin}/${clean}`
  }

  if (clean.startsWith('/')) {
    return `${siteOrigin}${clean}`
  }

  return `${siteOrigin}/${clean}`
}

export function StartView() {
  const [data, setData] = useState<StartPayload | null>(null)
  const [err, setErr] = useState<string | null>(null)

  const [view, setView] = useState<AuthView>('login')
  const [editing, setEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [authSheetOpen, setAuthSheetOpen] = useState(false)

  const [videoOpen, setVideoOpen] = useState(false)
  const [videoUrl, setVideoUrl] = useState('')
  const [videoError, setVideoError] = useState<string | null>(null)

  const agentName = useMasterAgentName()

  const { openImageManager } = useImageManager()

  function normalizeDraftFromHome(home: any): StartDraft {
    const head = String(home?.head || '')
    const text = String(home?.text || '')
    const video = String(home?.video || '')
    const theme = (editing ? (draft?.theme || home?.theme) : home?.theme) || 'light'
    const action = String(home?.action || 'community')

    // ✅ SNIPPET EXACTO: legacy image_bg -> images_bg
    const images_bg =
      Array.isArray(home?.images_bg) && home.images_bg.length
        ? home.images_bg.map((x: any) => String(x)).filter(Boolean)
        : home?.image_bg
          ? [String(home.image_bg)]
          : []

    return { head, text, video, theme, images_bg, action }
  }

  function openPickBgImage(opts: { mode: 'add' } | { mode: 'replace'; index: number }) {
    if (!isOwner || !editing) return
    if (!sellerId) return

    openImageManager({
        sellerId,
        onSelect: (img: any) => {
        // img.url viene como "/images/....."
        const rel = String(img?.url || '').replace(/^\/images\//, '').trim()
        if (!rel) return

        setDraft((d) => {
            const next = [...(d.images_bg || [])]

            if (opts.mode === 'add') {
            // evita duplicados
            if (!next.includes(rel)) next.push(rel)
            return { ...d, images_bg: next.slice(0, 5) } // límite 5 si quieres
            }

            // replace
            next[opts.index] = rel
            return { ...d, images_bg: next }
        })
        },
    })
  }

  function openVideoEditor() {
    setVideoUrl(draft.video || '')
    setVideoError(null)
    setVideoOpen(true)
  }

  function saveVideoUrl() {
    const url = videoUrl.trim()

    if (!url) {
      setVideoError('Pega una URL válida.')
      return
    }

    if (!/^https?:\/\//i.test(url)) {
      setVideoError('La URL debe iniciar con http:// o https://')
      return
    }

    setDraft((current) => ({
      ...current,
      video: url,
    }))

    setVideoOpen(false)
  }

  function removeVideo() {
    setDraft((current) => ({
      ...current,
      video: '',
    }))

    setVideoUrl('')
    setVideoError(null)
  }

  function removeBgImage(index: number) {
    setDraft((d) => {
        const next = [...(d.images_bg || [])]
        next.splice(index, 1)
        return { ...d, images_bg: next }
    })
  }

  // ✅ draft local (se reinicia al cargar home o al cancelar)
  const [draft, setDraft] = useState<StartDraft>(() => normalizeDraftFromHome({}))
  useEffect(() => {
    if (!data) return
    setDraft(normalizeDraftFromHome(data.home))
  }, [data?.sellerId]) // al cambiar seller
  useEffect(() => {
    if (!data) return
    // si llega home nuevo (reload), actualiza draft solo si NO está editando
    if (!editing) setDraft(normalizeDraftFromHome(data.home))
  }, [data?.home, editing])


  function actionToNext(action: any): string {
    const a = String(action || '').trim().toLowerCase()

    // ✅ legacy values (ajusta/crece esta lista cuando quieras)
    if (a === 'community') return '/community'
    if (a === 'moments') return '/moments'
    if (a === 'launchpad') return '/launchpad'
    if (a === 'onboarding') return '/onboarding'
    if (a === 'offers') return '/offers'
    if (a === 'home') return '/home'
    if (a === 'about') return '/about'
    if (a === 'courses') return '/courses'

    // si el seller mete una ruta directa tipo "/community" o "https://..."
    if (a.startsWith('/')) return a
    if (a.startsWith('http://') || a.startsWith('https://')) return a

    // default
    return '/community'
  }

  const sellerId = data?.sellerId || 0

  const { loggedIn, isLoading } = useCurrentUser()
  const router = useRouter()

  const home = data?.home || {}
  const isOwner = !!data?.isOwner
  const isOwnerPreview = loggedIn && isOwner
  const isBrandless = !!data?.isBrandless
  const isWhitelabel = !!data?.hasWhitelabel
  const companyStatus = Number(data?.companystatus ?? 0)
  const requiresApproval = companyStatus === 1
  const isOpenAccess = companyStatus === 0

  const theme = (editing ? (draft?.theme || home?.theme) : home?.theme) || 'light'

  const { brand } = useBranding(sellerId)
  const rawBrandLogo =
    theme === 'dark'
      ? brand?.logos?.horizontal_dark || brand?.logos?.horizontal || ''
      : brand?.logos?.horizontal || ''

  const brandLogo = useMemo(() => {
    return resolveBrandLogoHorizontal(
      rawBrandLogo,
      API_CONFIG.BASE_URL
    )
  }, [rawBrandLogo])
  
  const brandName = brand?.name || 'Hubee'

  async function reloadStart() {
    setErr(null)
    const res = await fetch('/api/start', { cache: 'no-store' })
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const json = (await res.json()) as StartPayload
    setData(json)
  }

  useEffect(() => {
    if (!authSheetOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
        document.body.style.overflow = prev
    }
  }, [authSheetOpen])

  useEffect(() => {
    let alive = true
    ;(async () => {
      try {
        const res = await fetch('/api/start', { cache: 'no-store' })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const json = (await res.json()) as StartPayload
        if (!alive) return
        setData(json)
      } catch (e: any) {
        if (!alive) return
        setErr(e?.message || 'Error')
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  async function saveStart() {
    setSaving(true)
    try {
      const res = await fetch('/api/start', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          head: draft.head,
          text: draft.text,
          video: draft.video,
          theme: draft.theme,
          images_bg: draft.images_bg,
          action: draft.action,
        }),
      })
      const out = await res.json().catch(() => null)
      if (!res.ok) throw new Error(out?.error || 'SAVE_FAILED')

      setEditing(false)
      await reloadStart()
    } catch (e: any) {
      setErr(e?.message || 'SAVE_FAILED')
    } finally {
      setSaving(false)
    }
  }

  const left = useMemo(() => {
    const head = String((editing ? draft.head : home?.head) || brandName || 'Bienvenido')
    const text = String((editing ? draft.text : home?.text) || 'Accede para continuar.')

    const rawImages =
      editing ? draft.images_bg : (Array.isArray(home?.images_bg) ? home.images_bg : (home?.image_bg ? [home.image_bg] : []))

    const images = (rawImages || [])
      .map((p: any) => String(p))
      .filter(Boolean)
      .map((p: string) => `/images/${p}`)

    const video = editing ? draft.video : String(home?.video || '')

    return { head, text, images, video }
  }, [editing, draft, home, brandName])

  const [idx, setIdx] = useState(0)
  useEffect(() => {
    setIdx(0)
  }, [left.images?.length])

  useEffect(() => {
    if (!left.images?.length || left.images.length <= 1) return
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % left.images.length)
    }, 5000)
    return () => clearInterval(t)
  }, [left.images])

  const nextUrl = actionToNext(editing ? draft.action : home?.action)

  const poweredPill = (
    <span className="text-xs text-white/80 rounded-full bg-white/10 px-3 py-1.5 backdrop-blur">
      Powered by Hubee
    </span>
  )

  useEffect(() => {
    // Espera a que data exista (así isOwner ya es real)
    if (!data || isLoading) return

    const isOwnerResolved = !!data.isOwner

    // Si está loggeado y NO es owner => fuera
    if (loggedIn && !isOwnerResolved) {
      router.replace(nextUrl || '/community')
    }
  }, [data, loggedIn, isLoading, nextUrl, router])

  if (data && loggedIn && !data.isOwner) {
    return null // o un loader mínimo
  }

  const editorFieldClassName = cn(
    'w-full rounded-xl border px-4 text-sm outline-none transition',
    'focus:ring-2',
    theme === 'dark'
      ? [
          'border-white/15 bg-black/25 text-white',
          'placeholder:text-white/55',
          'focus:border-white/25 focus:ring-white/15',
        ]
      : [
          'border-black/10 bg-white/90 text-zinc-900',
          'placeholder:text-zinc-500',
          'shadow-sm',
          'focus:border-black/20 focus:ring-black/10',
        ]
  )

  const ownerPreviewNotice = isOwnerPreview ? (
    <div className="mb-5 flex items-start gap-3 rounded-2xl border border-primary/20 bg-primary/[0.06] px-4 py-3">
      <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
        <Eye className="h-4 w-4" />
      </div>

      <div>
        <p className="text-sm font-medium text-foreground">
          Vista previa para visitantes
        </p>

        <p className="mt-0.5 text-xs leading-5 text-muted-foreground">
          Ya tienes una sesión iniciada como propietario. Este formulario es solo
          una demostración de lo que verán las personas que visiten tu página.
        </p>
      </div>
    </div>
  ) : null

  return (
  <div
    className={cn(
      'min-h-screen w-full bg-background text-foreground',
      theme === 'dark' && 'dark'
    )}
  >
    {/* =========================
        DESKTOP (igual que antes)
        ========================= */}
    <div className="hidden min-h-screen w-full lg:grid lg:grid-cols-2">
      {/* LEFT */}
      <div className="p-4 sm:p-6 lg:p-8 pb-[88px] lg:pb-8">
        <div className="relative lg:h-full rounded-[22px] overflow-hidden">
          {/* background crossfade */}
          {left.images?.length ? (
            <div className="absolute inset-0 pointer-events-none">
              {left.images.map((src, i) => (
                <div
                  key={`${src}-${i}`}
                  className="absolute inset-0 bg-cover bg-center transition-opacity duration-700"
                  style={{
                    backgroundImage: `url(${src})`,
                    opacity: i === idx ? 1 : 0,
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-foreground/10 to-foreground/0" />
          )}

          <div className="absolute inset-0 bg-black/35 pointer-events-none" />

          {/* top row */}
          <div className="absolute left-4 top-4 right-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              {brandLogo ? (
                <Image
                  key={`${theme}-${brandLogo}`}
                  src={brandLogo}
                  alt={brandName}
                  width={140}
                  height={36}
                  className="h-7 w-auto object-contain"
                  priority
                  unoptimized
                />
              ) : (
                <div className="text-white font-semibold tracking-tight truncate">{brandName}</div>
              )}
            </div>

            {isOwner ? (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => router.push('/')}
                  className="inline-flex items-center gap-2 rounded-full bg-black/35 px-3 py-1.5 text-xs font-medium text-white/90 backdrop-blur transition hover:bg-black/50 hover:text-white"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  Regresar
                </button>
                {editing ? (
                  <>
                    {/* 🌗 Theme toggle */}
                    <button
                      type="button"
                      onClick={() =>
                        setDraft((d: any) => ({
                          ...d,
                          theme: d.theme === 'dark' ? 'light' : 'dark',
                        }))
                      }
                      className={cn(
                        'text-xs transition rounded-full px-3 py-1.5 backdrop-blur flex items-center gap-1',
                        draft.theme === 'dark'
                          ? 'bg-white/20 text-white'
                          : 'bg-white/10 text-white/80'
                      )}
                      disabled={saving}
                    >
                      {draft.theme === 'dark' ? (
                        <>
                          <Moon className="h-3.5 w-3.5" />
                          Dark
                        </>
                      ) : (
                        <>
                          <Sun className="h-3.5 w-3.5" />
                          Light
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setEditing(false)
                        setDraft(normalizeDraftFromHome(home)) // cancelar = reset
                      }}
                      className="text-xs text-white/80 hover:text-white transition rounded-full bg-white/10 px-3 py-1.5 backdrop-blur"
                      disabled={saving}
                    >
                      Cancelar
                    </button>
                    <button
                      type="button"
                      onClick={saveStart}
                      className="text-xs text-white/90 hover:text-white transition rounded-full bg-white/20 px-3 py-1.5 backdrop-blur"
                      disabled={saving}
                    >
                      {saving ? 'Guardando…' : 'Guardar'}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={() => setEditing(true)}
                    className="text-xs text-white/80 hover:text-white transition rounded-full bg-white/10 px-3 py-1.5 backdrop-blur"
                  >
                    Editar
                  </button>
                )}
              </div>
            ) : home?.link ? (
              <a
                href={String(home.link)}
                className="text-xs text-white/80 hover:text-white transition rounded-full bg-white/10 px-3 py-1.5 backdrop-blur hidden"
              >
                Back to website →
              </a>
            ) : null}
          </div>

          {/* center content */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center p-6 sm:p-8 text-center">
            <div className="max-w-xl w-full">
              {editing ? (
                <div className="mx-auto max-w-lg space-y-3 pointer-events-auto">
                  <input
                    value={draft.head}
                    onChange={(e) =>
                      setDraft((current) => ({
                        ...current,
                        head: e.target.value,
                      }))
                    }
                    placeholder="Escribe el título principal"
                    className={cn(
                      editorFieldClassName,
                      'h-11'
                    )}
                  />
                  <textarea
                    value={draft.text}
                    onChange={(e) =>
                      setDraft((current) => ({
                        ...current,
                        text: e.target.value,
                      }))
                    }
                    placeholder="Escribe el mensaje que verán tus visitantes"
                    rows={3}
                    className={cn(
                      editorFieldClassName,
                      'resize-none py-3'
                    )}
                  />
                  <div
                    className={cn(
                      'overflow-hidden rounded-2xl border',
                      theme === 'dark'
                        ? 'border-white/15 bg-black/25'
                        : 'border-black/10 bg-white/90 shadow-sm'
                    )}
                  >
                    {draft.video ? (
                      <>
                        <div className="aspect-video w-full overflow-hidden bg-black">
                          <VideoEmbed
                            url={draft.video}
                            rounded="none"
                            variant="glass"
                            className="h-full w-full"
                          />
                        </div>

                        <div
                          className={cn(
                            'flex flex-col gap-3 border-t p-3 sm:flex-row sm:items-center sm:justify-between',
                            theme === 'dark'
                              ? 'border-white/10'
                              : 'border-black/10'
                          )}
                        >
                          <div className="min-w-0">
                            <p
                              className={cn(
                                'text-sm font-medium',
                                theme === 'dark'
                                  ? 'text-white'
                                  : 'text-zinc-900'
                              )}
                            >
                              Video de bienvenida
                            </p>

                            <p
                              className={cn(
                                'mt-0.5 truncate text-xs',
                                theme === 'dark'
                                  ? 'text-white/55'
                                  : 'text-zinc-500'
                              )}
                            >
                              {draft.video}
                            </p>
                          </div>

                          <div className="flex shrink-0 items-center gap-2">
                            <button
                              type="button"
                              onClick={openVideoEditor}
                              className={cn(
                                'inline-flex h-9 items-center gap-2 rounded-xl px-3 text-xs font-medium transition',
                                theme === 'dark'
                                  ? 'bg-white/10 text-white hover:bg-white/15'
                                  : 'bg-zinc-100 text-zinc-800 hover:bg-zinc-200'
                              )}
                            >
                              <Video className="h-4 w-4" />
                              Cambiar
                            </button>

                            <button
                              type="button"
                              onClick={removeVideo}
                              className={cn(
                                'inline-flex h-9 items-center gap-2 rounded-xl px-3 text-xs font-medium transition',
                                theme === 'dark'
                                  ? 'bg-red-500/10 text-red-300 hover:bg-red-500/20'
                                  : 'bg-red-50 text-red-600 hover:bg-red-100'
                              )}
                            >
                              <Trash2 className="h-4 w-4" />
                              Quitar
                            </button>
                          </div>
                        </div>
                      </>
                    ) : (
                      <button
                        type="button"
                        onClick={openVideoEditor}
                        className={cn(
                          'flex aspect-video w-full flex-col items-center justify-center gap-3 p-6 text-center transition',
                          theme === 'dark'
                            ? 'text-white/70 hover:bg-white/5 hover:text-white'
                            : 'text-zinc-600 hover:bg-zinc-50 hover:text-zinc-900'
                        )}
                      >
                        <div
                          className={cn(
                            'flex h-12 w-12 items-center justify-center rounded-2xl',
                            theme === 'dark'
                              ? 'bg-white/10 text-white'
                              : 'bg-zinc-100 text-zinc-700'
                          )}
                        >
                          <Video className="h-5 w-5" />
                        </div>

                        <div>
                          <p
                            className={cn(
                              'text-sm font-semibold',
                              theme === 'dark'
                                ? 'text-white'
                                : 'text-zinc-900'
                            )}
                          >
                            Agregar video
                          </p>

                          <p
                            className={cn(
                              'mt-1 text-xs',
                              theme === 'dark'
                                ? 'text-white/55'
                                : 'text-zinc-500'
                            )}
                          >
                            Pega una URL de YouTube, Vimeo u otra plataforma compatible.
                          </p>
                        </div>
                      </button>
                    )}
                  </div>

                  <select
                    value={draft.action}
                    onChange={(e) => setDraft((d) => ({ ...d, action: e.target.value }))}
                    className={cn(
                      'h-11 w-full rounded-lg px-4 text-sm outline-none transition',
                      'bg-white/5 border border-white/10 focus:ring-2 focus:ring-white/15 text-white glass-apple'
                    )}
                  >
                    <option value="launchpad">Abrir {agentName}</option>
                    <option value="moments">Momentos</option>
                    <option value="home">Ir a Inicio</option>
                    <option value="onboarding">Ir a Onboarding</option>
                    <option value="community">Ir a Comunidad</option>
                    <option value="courses">Ir a Cursos</option>
                  </select>

                  <p className="text-xs text-white/60">
                    Esto define a dónde se redirige después de login/registro.
                  </p>

                  <div className="pt-2">
                    <div className="flex items-center justify-between">
                      <div className="text-xs text-white/70">Fondos (máx 5)</div>

                      <button
                        type="button"
                        onClick={() => openPickBgImage({ mode: 'add' })}
                        className="inline-flex items-center gap-2 text-xs text-white/80 hover:text-white transition rounded-full bg-white/10 px-3 py-1.5 backdrop-blur"
                      >
                        <ImagePlus className="h-4 w-4" />
                        Agregar
                      </button>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-3">
                      {(draft.images_bg || []).map((p, i) => {
                        const src = `/images/${p}`
                        return (
                          <div
                            key={`${p}-${i}`}
                            className="group relative h-14 w-20 rounded-lg overflow-hidden border border-white/10 bg-white/5"
                          >
                            <button
                              type="button"
                              onClick={() => openPickBgImage({ mode: 'replace', index: i })}
                              className="absolute inset-0"
                              aria-label={`Cambiar imagen ${i + 1}`}
                            >
                              <img src={src} alt="" className="h-full w-full object-cover" />
                            </button>

                            <button
                              type="button"
                              onClick={() => removeBgImage(i)}
                              className="absolute right-1 top-1 opacity-0 group-hover:opacity-100 transition rounded-md bg-black/50 p-1 text-white"
                              aria-label={`Quitar imagen ${i + 1}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )
                      })}

                      {(!draft.images_bg || draft.images_bg.length === 0) ? (
                        <button
                          type="button"
                          onClick={() => openPickBgImage({ mode: 'add' })}
                          className="h-14 w-20 rounded-lg border border-dashed border-white/20 bg-white/5 text-white/70 hover:text-white hover:border-white/35 transition flex items-center justify-center"
                          aria-label="Agregar fondo"
                        >
                          <ImagePlus className="h-5 w-5" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-white text-2xl sm:text-3xl font-semibold leading-tight">
                    {left.head}
                  </div>

                  <div className="mt-3 text-white/85 text-sm sm:text-base">
                    {left.text}
                  </div>

                  {left.video ? (
                    <div className="mt-6">
                      <VideoEmbed
                        url={left.video}
                        rounded="2xl"
                        variant="glass"
                        className="max-w-xl mx-auto"
                      />
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>

          {/* DOTS */}
          {(left.images?.length || 0) > 1 ? (
            <div className="absolute left-6 sm:left-8 bottom-6 sm:bottom-8 flex items-center gap-2">
              {left.images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={cn(
                    'h-1.5 rounded-full transition-all duration-300',
                    i === idx ? 'w-6 bg-white/80' : 'w-2 bg-white/35 hover:bg-white/55'
                  )}
                />
              ))}
            </div>
          ) : null}

          {/* POWERED BY */}
          {isBrandless ? (
            <div className="absolute right-6 sm:right-8 bottom-6 sm:bottom-8">{poweredPill}</div>
          ) : (
            <a
              href="https://hubee.app"
              target="_blank"
              rel="noopener noreferrer"
              className="absolute right-6 sm:right-8 bottom-6 sm:bottom-8 text-xs text-white/80 hover:text-white transition rounded-full bg-white/10 px-3 py-1.5 backdrop-blur"
            >
              Powered by Hubee
            </a>
          )}
        </div>
      </div>

      {/* RIGHT */}
      <div className="flex items-center justify-center p-6 sm:p-10 lg:p-12 text-foreground">
        <div className="w-full max-w-lg">
          <div className="text-3xl font-semibold tracking-tight text-foreground">
            {isOwnerPreview
              ? view === 'login'
                ? 'Vista previa: iniciar sesión'
                : 'Vista previa: crear cuenta'
              : view === 'login'
                ? 'Bienvenido de regreso'
                : 'Crear una cuenta'}
          </div>

          <div className="mt-2 text-sm text-muted-foreground">
            {isOwnerPreview ? (
              <>
                Así verán esta página tus visitantes.{' '}
                <button
                  type="button"
                  className="text-foreground underline underline-offset-4"
                  onClick={() =>
                    setView((current) => (current === 'login' ? 'signup' : 'login'))
                  }
                >
                  Ver formulario de {view === 'login' ? 'registro' : 'inicio de sesión'}
                </button>
              </>
            ) : view === 'login' ? (
              <>
                ¿No tienes una cuenta?{' '}
                <button
                  type="button"
                  className="text-foreground hover:opacity-70 transition underline underline-offset-4"
                  onClick={() => setView('signup')}
                >
                  Regístrate
                </button>
              </>
            ) : (
              <>
                ¿Ya tienes una cuenta?{' '}
                <button
                  type="button"
                  className="text-foreground hover:opacity-70 transition underline underline-offset-4"
                  onClick={() => setView('login')}
                >
                  Inicia Sesión
                </button>
              </>
            )}
          </div>

          {!data && !err ? <div className="mt-6 text-sm text-muted-foreground">Cargando…</div> : null}
          {err ? <div className="mt-6 text-sm text-destructive">Error: {err}</div> : null}

          <div className="mt-6">
            {ownerPreviewNotice}

            <div
              className={cn(
                !data && !err && 'opacity-80',
                isOwnerPreview && 'pointer-events-none select-none opacity-75'
              )}
              aria-disabled={isOwnerPreview}
            >
              {view === 'login' ? (
                <AuthCard
                  sellerId={sellerId}
                  variant="inline"
                  chrome="none"
                  theme={theme}
                  next={nextUrl}
                  onRequestSignup={() => setView('signup')}
                />
              ) : (
                <AuthSignupCard
                  companyStatus={companyStatus}
                  isWhitelabel={isWhitelabel}
                  sellerId={sellerId}
                  variant="inline"
                  chrome="none"
                  theme={theme}
                  next={nextUrl}
                  onRequestLogin={() => setView('login')}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>

    {/* =========================
        MOBILE (Hero full + button + sheet)
        ========================= */}
    <div className="lg:hidden min-h-screen w-full">
      {/* Fullscreen HERO */}
      <div className="p-4 sm:p-6">
        <div className="relative min-h-[calc(100dvh-2rem)] rounded-[22px] overflow-hidden">
          {/* background crossfade */}
          {left.images?.length ? (
            <div className="absolute inset-0 pointer-events-none">
              {left.images.map((src, i) => (
                <div
                  key={`${src}-${i}`}
                  className="absolute inset-0 bg-cover bg-center transition-opacity duration-700"
                  style={{
                    backgroundImage: `url(${src})`,
                    opacity: i === idx ? 1 : 0,
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="absolute inset-0 bg-gradient-to-br from-foreground/10 to-foreground/0" />
          )}

          <div className="absolute inset-0 bg-black/35 pointer-events-none" />

          {/* top row */}
          <div className="absolute left-4 top-4 right-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              {brandLogo ? (
                <Image
                  key={`${theme}-${brandLogo}`}
                  src={brandLogo}
                  alt={brandName}
                  width={140}
                  height={36}
                  className="h-7 w-auto object-contain"
                  priority
                  unoptimized
                />
              ) : (
                <div className="text-white font-semibold tracking-tight truncate">{brandName}</div>
              )}
            </div>

            {isOwner ? (
              <button
                type="button"
                onClick={() => router.push('/settings/branding')}
                className="inline-flex items-center gap-2 rounded-full bg-black/35 px-3 py-1.5 text-xs font-medium text-white/90 backdrop-blur transition hover:bg-black/50 hover:text-white"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                Regresar
              </button>
            ) : home?.link ? (
              <a
                href={String(home.link)}
                className="rounded-full bg-white/10 px-3 py-1.5 text-xs text-white/80 backdrop-blur transition hover:text-white"
              >
                Back →
              </a>
            ) : null}
          </div>

          {/* center content */}
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center p-6 text-center">
            <div className="max-w-xl w-full">
              <div className="text-white text-2xl font-semibold leading-tight">{left.head}</div>
              <div className="mt-3 text-white/85 text-sm">{left.text}</div>

              {left.video ? (
                <div className="mt-6">
                  <VideoEmbed
                    url={left.video}
                    rounded="2xl"
                    variant="glass"
                    className="max-w-xl mx-auto"
                  />
                </div>
              ) : null}
            </div>
          </div>

          {/* DOTS */}
          {(left.images?.length || 0) > 1 ? (
            <div className="absolute left-6 bottom-6 flex items-center gap-2">
              {left.images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  aria-label={`Slide ${i + 1}`}
                  className={cn(
                    'h-1.5 rounded-full transition-all duration-300',
                    i === idx ? 'w-6 bg-white/80' : 'w-2 bg-white/35 hover:bg-white/55'
                  )}
                />
              ))}
            </div>
          ) : null}

          {/* POWERED BY */}
          {isBrandless ? (
            <div className="absolute right-6 bottom-6">{poweredPill}</div>
          ) : (
            <a
              href="https://hubee.app"
              target="_blank"
              rel="noopener noreferrer"
              className="absolute right-6 bottom-6 text-xs text-white/80 hover:text-white transition rounded-full bg-white/10 px-3 py-1.5 backdrop-blur"
            >
              Powered by Hubee
            </a>
          )}
        </div>
      </div>

      {/* Floating button */}
      <AnimatePresence>
        {!authSheetOpen && (
            <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4"
            >
                <div className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4">
                    <button
                    type="button"
                    onClick={() => setAuthSheetOpen(true)}
                    className={cn(
                        'w-full h-12 rounded-2xl font-semibold',
                        'bg-white text-black shadow-lg shadow-black/20',
                        'active:scale-[0.99] transition'
                    )}
                    >
                    ACCEDER
                    </button>
                </div>
            </motion.div>
        )}
      </AnimatePresence>

      {/* Bottom sheet */}
      <AnimatePresence>
        {authSheetOpen ? (
          <>
            {/* backdrop */}
            <motion.button
              type="button"
              aria-label="Cerrar"
              className="fixed inset-0 z-40 bg-black/60"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setAuthSheetOpen(false)}
            />

            {/* panel */}
            <motion.div
              className={cn(
                'fixed inset-x-0 bottom-0 z-50',
                'rounded-t-3xl border border-white/10 bg-background',
                'shadow-2xl'
              )}
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', stiffness: 380, damping: 38 }}
            >
              <div className="mx-auto w-full max-w-lg">
                {/* handle + header */}
                <div className="px-5 pt-3 pb-4">
                  <div className="mx-auto h-1.5 w-10 rounded-full bg-foreground/15" />
                  <div className="mt-4 flex items-start justify-between gap-4">
                    <div>
                      <div className="text-xl font-semibold tracking-tight text-foreground">
                        {view === 'login' ? 'Welcome back' : 'Create an account'}
                      </div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        {view === 'login' ? (
                          <>
                            ¿Aún no tienes cuenta?{' '}
                            <button
                              type="button"
                              className="text-foreground underline underline-offset-4"
                              onClick={() => setView('signup')}
                            >
                              Regístrate
                            </button>
                          </>
                        ) : (
                          <>
                            ¿Ya tienes cuenta?{' '}
                            <button
                              type="button"
                              className="text-foreground underline underline-offset-4"
                              onClick={() => setView('login')}
                            >
                              Inicia sesión
                            </button>
                          </>
                        )}
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => setAuthSheetOpen(false)}
                      className="rounded-full bg-foreground/10 px-3 py-1.5 text-sm text-foreground"
                    >
                      Cerrar
                    </button>
                  </div>
                </div>

                {/* content */}
                <div className="px-5 pb-6 max-h-[75dvh] overflow-auto">
                  {!data && !err ? <div className="mt-2 text-sm text-muted-foreground">Cargando…</div> : null}
                  {err ? <div className="mt-2 text-sm text-destructive">Error: {err}</div> : null}

                  <div className="mt-4">
                    {ownerPreviewNotice}

                    <div
                      className={cn(
                        !data && !err && 'opacity-80',
                        isOwnerPreview && 'pointer-events-none select-none opacity-75'
                      )}
                      aria-disabled={isOwnerPreview}
                    >
                      {view === 'login' ? (
                        <AuthCard
                          sellerId={sellerId}
                          variant="inline"
                          chrome="none"
                          theme={theme}
                          next={nextUrl}
                          onRequestSignup={() => setView('signup')}
                        />
                      ) : (
                        <AuthSignupCard
                          companyStatus={companyStatus}
                          isWhitelabel={isWhitelabel}
                          sellerId={sellerId}
                          variant="inline"
                          chrome="none"
                          theme={theme}
                          next={nextUrl}
                          onRequestLogin={() => setView('login')}
                        />
                      )}
                    </div>
                  </div>

                  <div className="mt-5 text-xs text-muted-foreground">
                    By continuing you agree to our Terms & Privacy.
                  </div>
                </div>
              </div>
            </motion.div>
          </>
        ) : null}
      </AnimatePresence>
    </div>
    <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
      <DialogContent className="max-w-lg">
        <div className="space-y-5">
          <div>
            <h2 className="text-lg font-semibold text-foreground">
              {draft.video ? 'Cambiar video' : 'Agregar video'}
            </h2>

            <p className="mt-1 text-sm text-muted-foreground">
              Pega la URL de un video de YouTube, Vimeo u otra plataforma compatible.
            </p>
          </div>

          <div className="space-y-2">
            <input
              value={videoUrl}
              onChange={(e) => {
                setVideoUrl(e.target.value)
                setVideoError(null)
              }}
              placeholder="https://..."
              className={cn(
                'h-11 w-full rounded-xl border bg-background px-3 text-sm text-foreground outline-none',
                'placeholder:text-muted-foreground',
                'focus:ring-2 focus:ring-primary/20',
                videoError
                  ? 'border-red-500'
                  : 'border-border'
              )}
              autoFocus
            />

            {videoError ? (
              <p className="text-xs text-red-500">
                {videoError}
              </p>
            ) : null}
          </div>

          {videoUrl.trim() && /^https?:\/\//i.test(videoUrl.trim()) ? (
            <div className="aspect-video overflow-hidden rounded-2xl border border-border bg-black">
              <VideoEmbed
                url={videoUrl.trim()}
                rounded="none"
                variant="glass"
                className="h-full w-full"
              />
            </div>
          ) : null}

          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setVideoOpen(false)}
              className="rounded-xl border border-border px-4 py-2 text-sm text-foreground transition hover:bg-muted"
            >
              Cancelar
            </button>

            <button
              type="button"
              onClick={saveVideoUrl}
              className="inline-flex items-center gap-2 rounded-xl bg-foreground px-4 py-2 text-sm font-medium text-background transition hover:opacity-90"
            >
              <Check className="h-4 w-4" />
              Usar video
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  </div>
)
}